import * as React from 'react';

import { AppDrawerContent } from '../AppDrawerLayout/components';
import { ContentContainer, DrawerContainer, DrawerLayoutContainer } from './BaseDrawerLayout.styles';
import { DrawerLayoutContext, DrawerLayoutProvider, useDrawerLayoutContext } from './context';
import ToggleButtonDrawer from './ToggleButtonDrawer';

interface DrawerProps {
  isOpen: boolean;
  // Valid CSS `string` that could be used in CSS rules like `width` or `min-width`;
  expandedWidth?: string;
  // Valid CSS `string` that could be used in CSS rules like `width` or `min-width`;
  collapsedWidth?: string;
}

const Drawer = ({ children }: React.PropsWithChildren<DrawerProps>) => {
  // Dependencies
  const { isOpened, expandedDrawerWidth, collapsedDrawerWidth, setDisplayToggle } = useDrawerLayoutContext();

  return (
    <DrawerContainer
      $isOpen={isOpened}
      $expandedWidth={expandedDrawerWidth}
      $collapsedWidth={collapsedDrawerWidth}
      onMouseEnter={() => setDisplayToggle(true)}
      onMouseLeave={() => setDisplayToggle(false)}
    >
      {children}
    </DrawerContainer>
  );
};

interface ContentProps {}

const Content = ({ children }: React.PropsWithChildren<ContentProps>) => {
  // Dependencies
  const { collapsedDrawerWidth, expandedDrawerWidth, isOpened } = useDrawerLayoutContext();

  return (
    <ContentContainer $collapsedWidth={collapsedDrawerWidth} $expandedWidth={expandedDrawerWidth} $isOpen={isOpened}>
      {children}
    </ContentContainer>
  );
};

export const DEFAULT_EXPANDED_WIDTH = 'unset';
export const DEFAULT_COLLAPSED_WIDTH = '2rem';

export interface BaseDrawerLayoutProps extends Partial<DrawerProps> {
  mainContent: React.ReactNode;
  onChange?: (isOpen: boolean) => void;
}

const DrawerLayout = ({
  isOpen: _isOpen = false,
  expandedWidth = DEFAULT_EXPANDED_WIDTH,
  collapsedWidth = DEFAULT_COLLAPSED_WIDTH,
  mainContent,
  children,
  onChange,
}: React.PropsWithChildren<BaseDrawerLayoutProps>) => {
  // State
  const [isOpen, setIsOpen] = React.useState(_isOpen || false);
  const [displayToggle, setDisplayToggle] = React.useState(false);

  // Derived State
  const drawerLayoutContext: DrawerLayoutContext = {
    isOpened: isOpen,
    setIsOpen,
    collapsedDrawerWidth: collapsedWidth,
    expandedDrawerWidth: expandedWidth,
    setDisplayToggle,
  };

  // Handlers
  const handleCollapseClick = () => {
    return setIsOpen((currentIsOpen) => {
      onChange?.(!currentIsOpen);
      return !currentIsOpen;
    });
  };

  // Helpers
  const drawerProps = {
    isOpen,
    expandedWidth,
    collapsedWidth,
  };

  return (
    <DrawerLayoutProvider value={drawerLayoutContext}>
      <DrawerLayoutContainer>
        <>
          <Drawer {...drawerProps}>
            <>
              <AppDrawerContent />
              {displayToggle ? <ToggleButtonDrawer isExpanded={isOpen} onToggle={handleCollapseClick} /> : null}
            </>
          </Drawer>
          <Content>{mainContent}</Content>
          {children}
        </>
      </DrawerLayoutContainer>
    </DrawerLayoutProvider>
  );
};

export default DrawerLayout;
